import React, { useCallback } from "react";
import { ChevronDown, Loader2, RefreshCw, SlidersHorizontal, Target, UserPlus } from "lucide-react";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import TextButton from "@/shared/components/ui/Button/TextButton";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import ResultsCount from "@/shared/components/ui/Status/ResultsCount";
import ProspectQueryBar from "./ProspectQueryBar";
import ProspectEmptyState from "./ProspectEmptyState";
import { renderProspectCell, type ProspectCellContext } from "./ProspectResultCells";
import { PROSPECT_SKELETON_ROWS, PROSPECT_TABLE_PROPS } from "../../constants/prospectConfig";
import { PROSPECT_RESULT_COLUMNS } from "../../constants/prospectColumns";
import type { ProspectItem, SavedSearchItem, SearchCampaign } from "../../types";

const CustomTableComponent = CustomTable as React.ComponentType<any>;
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>;
const TextButtonComponent = TextButton as React.ComponentType<any>;
const CommonDropdownMenuComponent = CommonDropdownMenu as React.ComponentType<any>;

interface ProspectResultsProps {
  queryName: string;
  queryText: string;
  onQueryChange: (value: string) => void;
  onSearch: (query: string) => void;
  onNewSearch: () => void;
  savedSearches: SavedSearchItem[];
  suggestions: string[];
  selectedSavedId: string | number | null;
  onSelectSaved: (saved: SavedSearchItem) => void;
  prospects: ProspectItem[];
  isSearching: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onFindMore: () => void;
  selected: ProspectItem[];
  onSelectedChange: (items: ProspectItem[]) => void;
  isBulkImporting: boolean;
  onBulkImport: () => void;
  /** Set when the results came from a campaign's ICP: "Add to <campaign>" goes straight there;
   *  otherwise the campaign is picked in a chooser. */
  campaign: SearchCampaign | null;
  onAddToCampaign: () => void;
  onOpenFilters: () => void;
  activeFilterCount: number;
  onRetry: () => void;
  cellContext: ProspectCellContext;
}

export default function ProspectResults({
  queryName,
  queryText,
  onQueryChange,
  onSearch,
  onNewSearch,
  savedSearches,
  suggestions,
  selectedSavedId,
  onSelectSaved,
  prospects,
  isSearching,
  hasMore,
  isLoadingMore,
  onFindMore,
  selected,
  onSelectedChange,
  isBulkImporting,
  onBulkImport,
  campaign,
  onAddToCampaign,
  onOpenFilters,
  activeFilterCount,
  onRetry,
  cellContext,
}: ProspectResultsProps) {
  const renderCell = useCallback(
    (item: ProspectItem, columnKey: string) => renderProspectCell(item, columnKey, cellContext),
    [cellContext],
  );

  const count = prospects.length;

  return (
    // Fills the tab so the table never runs past the screen.
    <div className="flex flex-col gap-4 w-full h-full px-6 pb-4">
      <ProspectQueryBar
        queryName={queryName}
        value={queryText}
        onChange={onQueryChange}
        onSearch={onSearch}
        onNewSearch={onNewSearch}
        savedSearches={savedSearches}
        selectedSavedId={selectedSavedId}
        onSelectSaved={onSelectSaved}
      />

      <div className="flex min-h-8 items-center justify-between gap-3 flex-wrap">
        <ResultsCount isLoading={isSearching || isLoadingMore} count={count} loadingText="Finding contacts" noun="Contacts" />

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {selected.length > 0 && (
            <>
              <CommonDropdownMenuComponent
                ariaLabel="Move selected contacts"
                isDisabled={isBulkImporting}
                className="min-w-[200px]"
                actions={[
                  { id: "contacts", name: "Move to Contacts", icon: <UserPlus size={14} />, onClick: onBulkImport },
                  {
                    id: "campaign",
                    name: campaign ? `Move to ${campaign.name}` : "Move to campaign",
                    icon: <Target size={14} />,
                    onClick: onAddToCampaign,
                  },
                ]}
                triggerBtn={
                  <button
                    type="button"
                    disabled={isBulkImporting}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary text-white text-xs font-medium shadow-[0_4px_4px_0_rgba(255,255,255,0.25)_inset] disabled:opacity-60"
                  >
                    {isBulkImporting && <Loader2 size={14} className="animate-spin" />}
                    Move ({selected.length}) to
                    <ChevronDown size={14} />
                  </button>
                }
              />
              <SecondaryButtonComponent
                title="Clear Selection"
                onClick={() => onSelectedChange([])}
                className="!h-8 text-xs px-3 shadow-none"
              />
            </>
          )}
          <SecondaryButtonComponent
            title={activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
            icon={<SlidersHorizontal size={14} className={activeFilterCount > 0 ? "text-secondary" : "text-gray-600"} />}
            onClick={onOpenFilters}
            disabled={isSearching}
            className={`!h-8 text-xs px-3 shadow-none ${activeFilterCount > 0 ? "border-secondary text-secondary" : ""}`}
          />
          {!isSearching && hasMore && (
            <TextButtonComponent
              onClick={onFindMore}
              isLoading={isLoadingMore}
              loadingText="Finding more..."
              tooltip="Search again for more people who match, skipping those already shown"
            >
              <span className="flex items-center gap-1">
                <RefreshCw size={12} /> Find more
              </span>
            </TextButtonComponent>
          )}
        </div>
      </div>

      {!isSearching && count === 0 ? (
        <ProspectEmptyState
          onRetry={onRetry}
          onNewSearch={onNewSearch}
          suggestions={suggestions}
          onPickSuggestion={onSearch}
        />
      ) : (
        <div className="flex-1 min-h-0 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-xs">
          <CustomTableComponent
            data={isSearching ? PROSPECT_SKELETON_ROWS : prospects}
            columns={PROSPECT_RESULT_COLUMNS}
            renderCell={renderCell}
            enableSelection={!isSearching}
            selectedItems={selected}
            onSelectionChange={onSelectedChange}
            onRowClick={isSearching ? undefined : cellContext.onOpen}
            isLoadingMore={!isSearching && isLoadingMore}
            loadingMoreText="Finding more matches. They'll appear here as they're verified."
            {...PROSPECT_TABLE_PROPS}
            height="100%"
          />
        </div>
      )}
    </div>
  );
}
